import * as React from 'react'
import { composeRefs } from '@radix-ui/react-compose-refs'
import { Primitive } from '@radix-ui/react-primitive'
import { experimental_VGrid as VGrid, Virtualizer, VList } from 'virtua'

const VirtualizedContext = React.createContext<{
	scrollRef: React.Ref<HTMLElement>
	withScrollRef: boolean
}>({
	scrollRef: null,
	withScrollRef: false,
})

function useVirtualized() {
	const context = React.use(VirtualizedContext)

	if (!context) {
		throw new Error('useVirtualized must be used within a <Virtualized />')
	}

	return context
}

const Virtualized = ({
	ref,
	children,
	...props
}: React.ComponentProps<typeof Primitive.div>) => {
	const scrollRef = React.useRef<HTMLElement>(null)

	return (
		<VirtualizedContext value={{ scrollRef, withScrollRef: true }}>
			<Primitive.div ref={composeRefs(scrollRef, ref)} {...props}>
				{children}
			</Primitive.div>
		</VirtualizedContext>
	)
}
Virtualized.displayName = 'Virtualized'

export type VirtualizedListProps = {
	orientation?: 'vertical' | 'horizontal'
} & Omit<React.ComponentProps<typeof VList>, 'horizontal'>

const VirtualizedList = ({
	ref,
	orientation = 'vertical',
	...props
}: VirtualizedListProps) => {
	const { withScrollRef } = useVirtualized()

	if (withScrollRef) {
		throw new Error(
			'<VirtualizedList /> must not be used within a <Virtualized />'
		)
	}

	return (
		<VList ref={ref} horizontal={orientation === 'horizontal'} {...props} />
	)
}
VirtualizedList.displayName = 'VirtualizedList'

const VirtualizedGrid = VGrid

const VirtualizedVirtualizer = ({
	ref,
	...props
}: Omit<React.ComponentProps<typeof Virtualizer>, 'scrollRef'>) => {
	const { scrollRef, withScrollRef } = useVirtualized()

	return (
		<Virtualizer
			ref={ref}
			scrollRef={
				withScrollRef ? (scrollRef as React.RefObject<HTMLElement>) : undefined
			}
			{...props}
		/>
	)
}
VirtualizedVirtualizer.displayName = 'VirtualizedVirtualizer'

export { Virtualized, VirtualizedList, VirtualizedGrid, VirtualizedVirtualizer }
